/**
 * Axios instance shared by every query and mutation.
 *
 * Responsibilities: base URL from the Vite env, bearer injection, and a single
 * place where a 401 tears down the session.
 */

import axios, { AxiosError, type AxiosInstance } from 'axios';
import type { ApiError } from '@shared/types';
import { API_BASE_URL } from './endpoints';
import { messageForCode } from './errorMessages';
import { getAccessToken, signOutNow } from '@/store/auth';

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 20_000,
});

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

/** Set by App so a 401 can route without a full page reload. */
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    if (error.response?.status === 401) {
      signOutNow();
      onUnauthorized?.();
    }
    return Promise.reject(error);
  },
);

/**
 * Turn any thrown value into something safe to render.
 *
 * The server's `error` field is developer-facing English and is deliberately
 * never shown; the `code` is translated instead (see `errorMessages.ts`).
 * Network failures and timeouts keep their own wording, because «Network Error»
 * in a payments tool reads like money lost — and the distinction between "the
 * request never left" and "the request may have been applied" matters more in a
 * confirmation flow, not less.
 */
export function toMessage(error: unknown, fallback = 'Что-то пошло не так.'): string {
  if (axios.isAxiosError<ApiError>(error)) {
    if (error.code === 'ECONNABORTED') {
      return 'Сервер не ответил вовремя. Действие могло не сохраниться — обновите страницу, прежде чем повторять.';
    }
    if (error.response === undefined) {
      return 'Не удаётся связаться с сервером. Проверьте соединение и попробуйте снова.';
    }

    const translated = messageForCode(error.response.data?.code, error.response.status);
    if (translated) return translated;
  }
  return fallback;
}

/**
 * Field-level validation errors, when the server returns them.
 *
 * Only the field names are taken from the response: the messages inside
 * `details` are English Zod strings, so each field gets a Russian message here
 * instead. Client-side validation already carries the specific wording — this
 * is the backstop for a rule only the server knows about.
 */
export function toFieldErrors(error: unknown): Record<string, string> {
  if (!axios.isAxiosError<ApiError>(error)) return {};
  const details = error.response?.data?.details;
  if (!details || typeof details !== 'object' || Array.isArray(details)) return {};

  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(details as Record<string, unknown>)) {
    const hasMessage =
      typeof value === 'string' || (Array.isArray(value) && typeof value[0] === 'string');
    if (hasMessage) result[key] = 'Сервер не принял это значение.';
  }
  return result;
}
